import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { useWeb3Auth } from '../../providers/Web3AuthProvider';
import {
	getCombinedKarmaBalance,
	getKusdBalance,
	formatCurrency,
} from '../../utils/wallet-helpers';
import { RefreshCw, Wallet } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { type Provider } from 'ethers';
import { MemberReadProvider } from '../../lib/member-wallet';

interface CreditBalanceProps {
	userId: Id<'users'>;
}

export function CreditBalance({ userId }: CreditBalanceProps) {
	const credits = useQuery(api.credits.getBalance, { userId });
	const { walletAddress, user, memberSigner: activeAccount } = useWeb3Auth();

	const [karmaBalance, setKarmaBalance] = useState<string>('0');
	const [kusdBalance, setKusdBalance] = useState<string>('0');
	const [isLoading, setIsLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [ethersProvider, setEthersProvider] = useState<Provider | null>(null);
	const [baseProvider, setBaseProvider] = useState<Provider | null>(null);
	const [polygonProvider, setPolygonProvider] = useState<Provider | null>(null); // For KUSD on Polygon
	const [providerWalletAddress, setProviderWalletAddress] = useState<string | null>(
		null
	);
	const balanceReadGeneration = React.useRef(0);

	const syncWalletBalance = useMutation(api.credits.syncWalletBalance);

	useEffect(() => {
		balanceReadGeneration.current += 1;
		if (
			!activeAccount ||
			!walletAddress ||
			activeAccount.address.toLowerCase() !== walletAddress.toLowerCase()
		) {
			setEthersProvider(null);
			setBaseProvider(null);
			setPolygonProvider(null);
			setProviderWalletAddress(null);
			return;
		}

		const ethProvider = new MemberReadProvider(activeAccount, 1);
		const baseProviderNew = new MemberReadProvider(activeAccount, 8453);
		const polygonProviderNew = new MemberReadProvider(activeAccount, 137);
		setEthersProvider(ethProvider);
		setBaseProvider(baseProviderNew);
		setPolygonProvider(polygonProviderNew);
		setProviderWalletAddress(walletAddress);

		return () => {
			ethProvider.destroy();
			baseProviderNew.destroy();
			polygonProviderNew.destroy();
		};
	}, [activeAccount, walletAddress]);

	useEffect(() => {
		const generation = ++balanceReadGeneration.current;
		const isCurrent = () => balanceReadGeneration.current === generation;

		// If core dependencies are missing, we ensure the component is in a loading state.
		if (
			!walletAddress ||
			!providerWalletAddress ||
			providerWalletAddress.toLowerCase() !== walletAddress.toLowerCase() ||
			!ethersProvider ||
			!baseProvider
		) {
			if (!isLoading) {
				// This ensures we go back to loading state if dependencies become unavailable
				// after a successful load (e.g., wallet disconnects).
				setIsLoading(true);
			}
			console.log(
				'[CreditBalance] Effect: Dependencies (walletAddress, ethersProvider, or baseProvider) not fully available. isLoading:',
				isLoading
			);
			return; // Don't proceed to fetch if dependencies are not met
		}

		// Dependencies (walletAddress AND both providers) are available. Proceed to fetch.
		async function fetchAndSetBalances() {
			console.log(
				'[CreditBalance] Effect: Dependencies met. Starting fetch. WalletAddress:',
				walletAddress
			);
			setIsLoading(true); // Set loading true for the duration of this fetch operation
			setError(null);

			// Add explicit null check here for TypeScript
			if (!ethersProvider || !baseProvider || !walletAddress) {
				console.error(
					'[CreditBalance] ethersProvider, baseProvider, or walletAddress became null unexpectedly within fetchAndSetBalances.'
				);
				setError('Internal error: Wallet connection lost.');
				setIsLoading(false);
				return;
			}

			try {
				// Fetch KARMA balance from both chains
				const karmaBalances = await getCombinedKarmaBalance(
					ethersProvider,
					baseProvider,
					walletAddress
				);
				if (!isCurrent()) return;
				setKarmaBalance(karmaBalances.total);

				// Fetch KUSD balance using Polygon provider (KUSD is on Polygon, not Base)
				let stablecoinBalance = '0';
				if (polygonProvider) {
					const network = await polygonProvider.getNetwork();
					if (!isCurrent()) return;
					if (network.chainId === 137n) {
						// Polygon Mainnet
						stablecoinBalance = await getKusdBalance(
							polygonProvider,
							walletAddress
						);
						if (!isCurrent()) return;
					} else {
						console.warn(
							'[CreditBalance] KUSD balance not fetched: polygon provider not on Polygon. Current chain:',
							network.chainId
						);
					}
				}
				setKusdBalance(stablecoinBalance);

				// Sync KARMA balance with Convex database
				try {
					if (!isCurrent()) return;
					if (userId) {
						await syncWalletBalance({
							userId: userId,
							walletAddress: walletAddress,
							tokenBalance: parseFloat(karmaBalances.total),
						});
					} else if (user?.email) {
						await syncWalletBalance({
							userId: '', // Backend handles lookup by email or walletAddress
							email: user.email,
							walletAddress: walletAddress,
							tokenBalance: parseFloat(karmaBalances.total),
						});
					} else {
						// Fallback if only walletAddress is effectively available for identification
						await syncWalletBalance({
							userId: '',
							walletAddress: walletAddress,
							tokenBalance: parseFloat(karmaBalances.total),
						});
					}
				} catch (syncError) {
					if (!isCurrent()) return;
					console.error('Error syncing wallet balance:', syncError);
					// Continue displaying balance even if sync fails
				}
			} catch (err) {
				if (!isCurrent()) return;
				console.error('Error fetching token balance:', err);
				setError('Failed to load token balance');
			} finally {
				if (isCurrent()) setIsLoading(false);
			}
		}

		fetchAndSetBalances();
		return () => {
			if (balanceReadGeneration.current === generation) {
				balanceReadGeneration.current += 1;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		walletAddress,
		ethersProvider,
		baseProvider,
		polygonProvider,
		providerWalletAddress,
		userId,
		user,
		syncWalletBalance,
	]); // isLoading is intentionally not in deps

	const handleRefreshBalance = async () => {
		console.log(
			'[CreditBalance] handleRefreshBalance clicked. WalletAddress:',
			walletAddress,
			'EthersProvider:',
			ethersProvider ? 'Available' : 'Not Available',
			'PolygonProvider:',
			baseProvider ? 'Available' : 'Not Available'
		);
		if (
			!walletAddress ||
			!providerWalletAddress ||
			providerWalletAddress.toLowerCase() !== walletAddress.toLowerCase() ||
			!ethersProvider ||
			!baseProvider
		) {
			return;
		}
		const generation = ++balanceReadGeneration.current;
		const isCurrent = () => balanceReadGeneration.current === generation;

		setIsRefreshing(true);
		setError(null);

		try {
			// Fetch KARMA balance from both chains
			const refreshedKarmaBalances = await getCombinedKarmaBalance(
				ethersProvider,
				baseProvider,
				walletAddress
			);
			if (!isCurrent()) return;
			setKarmaBalance(refreshedKarmaBalances.total);

			// Fetch KUSD balance using Polygon provider (KUSD is on Polygon, not Base)
			let refreshedStablecoinBalance = kusdBalance; // Keep existing if provider not available
			if (polygonProvider) {
				const network = await polygonProvider.getNetwork();
				if (!isCurrent()) return;
				if (network.chainId === 137n) {
					// 137n is Polygon Mainnet
					refreshedStablecoinBalance = await getKusdBalance(
						polygonProvider,
						walletAddress
					);
					if (!isCurrent()) return;
				} else {
					console.warn(
						'[CreditBalance] KUSD balance not refreshed: polygon provider not on Polygon. Current chain:',
						network.chainId
					);
				}
			}
			setKusdBalance(refreshedStablecoinBalance);

			// Sync KARMA balance with Convex database
			if (!isCurrent()) return;
			if (userId) {
				await syncWalletBalance({
					userId: userId,
					walletAddress: walletAddress,
					tokenBalance: parseFloat(refreshedKarmaBalances.total),
				});
			}
			if (!isCurrent()) return;

			toast({
				title: 'Balances Updated',
				description: 'Your wallet balances have been refreshed.',
			});
		} catch (error) {
			if (!isCurrent()) return;
			console.error('Error refreshing token balances:', error);
			setError('Failed to refresh token balances');
			toast({
				title: 'Update Failed',
				description: 'Could not refresh wallet balances.',
				variant: 'destructive',
			});
		} finally {
			if (isCurrent()) setIsRefreshing(false);
		}
	};

	if (isLoading) {
		return (
			<div className='flex items-center justify-center h-16 w-full'>
				<div className='animate-pulse h-16 w-full bg-pink-800/20 rounded-lg'></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className='flex flex-col items-center justify-center p-4 rounded-xl bg-red-900/20 border border-red-800/40'>
				<span className='text-lg text-red-300'>
					{credits?.balance.toLocaleString() || '0'} KARMA (Error
					loading live balance)
				</span>
			</div>
		);
	}

	return (
		<div className='relative'>
			{isRefreshing && (
				<div className='absolute right-3 top-3 z-10'>
					<RefreshCw className='animate-spin h-5 w-5 text-pink-400' />
				</div>
			)}

			<div
				className='flex flex-row items-center px-6 py-4 rounded-xl bg-gradient-to-br from-pink-600/30 to-purple-800/30 shadow-[0_0_15px_rgba(236,72,153,0.3)] text-center relative cursor-pointer hover:from-pink-600/40 hover:to-purple-800/40 transition-all duration-300'
				onClick={handleRefreshBalance}
			>
				<div className='flex gap-2 items-center mt-2'>
					<Wallet className='h-8 w-8 text-primary dark:text-orange-500 mr-2' />
					{/* <div className='text-lg font-bold text-white'>WALLET</div> */}
					<div className='flex flex-col items-center pr-4'>
						<div className='text-2xl font-bold bg-gradient-to-r from-sky-300 to-sky-100 bg-clip-text text-transparent'>
							{walletAddress
								? formatCurrency(karmaBalance, 2)
								: credits?.plan === 'unlimited'
									? '∞'
									: credits?.balance?.toLocaleString() || '0'}
						</div>
						<div className='text-xl text-sky-400 font-semibold'>
							$KARMA
						</div>
					</div>
					{/* {credits?.plan === 'unlimited' && (
						<Badge className='bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow-[0_0_10px_rgba(251,191,36,0.4)]'>
							UNLIMITED
						</Badge>
					)} */}
					{/* {credits?.plan === 'wallet' && (
						<Badge className='bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-[0_0_10px_rgba(6,182,212,0.4)]'>
							WALLET
						</Badge>
					)} */}

					{/* KUSD Balance Display - only if wallet is connected */}
					{walletAddress && (
						<div className='flex flex-col items-center border-l border-pink-700/50 pl-6'>
							<div className='text-2xl font-bold bg-gradient-to-r from-pink-300 to-pink-100 bg-clip-text text-transparent'>
								{formatCurrency(kusdBalance, 2)}
							</div>
							<div className='text-xl text-pink-400 font-semibold'>
								$KUSD
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
